/**
 * Bağlantı durumu — "bu cihazın internet erişimi var mı" sorusunun tek cevabı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `navigator.onLine` tek başına kullanılmıyor
 * ═══════════════════════════════════════════════════════════════════════════
 * `navigator.onLine` yalnızca "bir ağ arayüzü bağlı mı" diyor. Wi-Fi'ye bağlı
 * ama modemin hattı kopmuş bir bilgisayarda `true` döner — kullanıcı hiçbir
 * sayfayı açamazken tarayıcı "çevrimiçiyim" der. Ters yönü de doğru: bazı
 * tarayıcılar VPN ve sanal arayüzlerde yanlış negatif verir.
 *
 * Bu yüzden üç kaynak birleştiriliyor:
 *
 *   1. `offline` / `online` olayları — ANINDA haber, en hızlı sinyal.
 *   2. Gerçek isteklerin başarısızlığı — `fetch` ağ seviyesinde patladığında
 *      (`TypeError`) bu, elimizdeki en dürüst kanıt: gerçekten deneyip
 *      ulaşamadık.
 *   3. Yoklama — kopuk durumdayken kendi sunucumuza küçük bir istek. Dönüşün
 *      200 olması gerekmiyor; 404 bile "sunucuya ULAŞTIK" demek, yani hat var.
 *
 * ---------------------------------------------------------------------------
 * Neden React dışında bir depo
 * ---------------------------------------------------------------------------
 * Durumu bildiren yer `lib/api.ts` — React ağacının dışında, bir modül. Bunu
 * bir context'e bağlasaydık istek katmanının React'e bağımlı olması gerekirdi.
 * Burası saf bir abonelik deposu; React tarafı `useSyncExternalStore` ile
 * okuyor (`components/connection-overlay.tsx`).
 */

export type ConnectionStatus = 'online' | 'offline';

/** Kopukken yoklama aralığı. Kısa: kullanıcı hattın döndüğünü hemen görmeli. */
const PROBE_INTERVAL_MS = 3_000;

/**
 * Yoklama hedefi — KENDİ kökümüz.
 *
 * Aynı origin olduğu için CORS yok ve sunucunun IP kısıtına takılmıyor
 * (kullanıcı zaten bu sayfayı oradan aldı). API'yi yoklamak yanlış olurdu:
 * API'nin düşmesi "internet yok" demek değil ve kullanıcıya yanlış cümleyi
 * gösterirdi.
 */
const PROBE_PATH = '/favicon.ico';

let status: ConnectionStatus = 'online';
const listeners = new Set<() => void>();
let probeTimer: ReturnType<typeof setInterval> | null = null;

function emit(): void {
  for (const listener of listeners) listener();
}

function setStatus(next: ConnectionStatus): void {
  if (status === next) return;
  status = next;

  if (next === 'offline') startProbing();
  else stopProbing();

  emit();
}

/**
 * Kopukken sunucuyu yokla.
 *
 * `online` olayını beklemek yetmiyor: olay yalnızca ARAYÜZ durumu değişince
 * çıkıyor, hattın ucundaki kopukluk düzeldiğinde çıkmıyor. Yoklama olmadan
 * kullanıcı, internet geri geldiği hâlde "bağlantı yok" ekranında kalırdı.
 */
function startProbing(): void {
  if (probeTimer !== null || typeof window === 'undefined') return;
  probeTimer = setInterval(() => void probe(), PROBE_INTERVAL_MS);
}

function stopProbing(): void {
  if (probeTimer === null) return;
  clearInterval(probeTimer);
  probeTimer = null;
}

async function probe(): Promise<boolean> {
  if (typeof window === 'undefined') return true;
  try {
    /* `no-store` + zaman damgası: araya giren hiçbir önbellek "başarılı"
       cevabı taklit edemesin. Önbellekten gelen 200, kopuk hatta da gelir. */
    await fetch(`${PROBE_PATH}?c=${Date.now()}`, { method: 'HEAD', cache: 'no-store' });
    setStatus('online');
    return true;
  } catch {
    setStatus('offline');
    return false;
  }
}

/** Şu anki durum. Sunucuda her zaman `online` — orada kavramın karşılığı yok. */
export function connectionStatus(): ConnectionStatus {
  return status;
}

export function isOffline(): boolean {
  return status === 'offline';
}

export function subscribeConnection(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * "Denedim, ulaşamadım."
 *
 * `lib/api.ts` ve akış istemcisi ağ hatası aldığında çağırıyor. Tarayıcının
 * ne dediğine bakılmıyor — elimizde başarısız bir istek var, bu yeterli
 * kanıt.
 */
export function reportOffline(): void {
  setStatus('offline');
}

/**
 * İstek KESİLDİ mi — "denedim, ulaşamadım" DEĞİL, "vazgeçtim".
 *
 * 🔴 İptal bir ağ hatası değil ve `reportOffline()` çağırmamalı: kopukluk
 * perdesi açılır ve kullanıcı bağlantısı sapasağlamken "bağlantı yok" okur.
 * Gerçek bir örnekle bulundu — ajan kutusundaki kampanya araması her tuş
 * vuruşunda uçuştaki isteği kesiyor.
 *
 * `fetch` iptali `DOMException` fırlatıyor; `AbortSignal.timeout()` de aynı
 * sınıfı ama `TimeoutError` adıyla atıyor, o yüzden ad kontrolü şart —
 * zaman aşımı gerçekten "ulaşamadım" demek ve perdeyi hak ediyor.
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Başarılı bir istek geldi: hat var. */
export function reportOnline(): void {
  setStatus('online');
}

/**
 * Tarayıcı olaylarına bağlanır. Uygulama başına BİR kez çağrılıyor
 * (`components/connection-overlay.tsx`), tekrar çağrılması zararsız.
 */
export function watchConnection(): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const goOffline = () => setStatus('offline');
  /* `online` olayında doğrudan "bağlandık" demiyoruz: olay yalnızca arayüzün
     geri geldiğini söylüyor, karşı tarafa ulaşıp ulaşamadığımızı değil. */
  const maybeOnline = () => void probe();

  window.addEventListener('offline', goOffline);
  window.addEventListener('online', maybeOnline);

  // İlk boyamada tarayıcı zaten kopuk olduğunu biliyorsa ekranı hemen ver.
  if (navigator.onLine === false) setStatus('offline');

  return () => {
    window.removeEventListener('offline', goOffline);
    window.removeEventListener('online', maybeOnline);
    stopProbing();
  };
}

/** Kopukluk durumunda elle yoklama — "yeniden dene" düğmesi bunu çağırıyor. */
export function retryConnection(): Promise<boolean> {
  return probe();
}
